import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { MainLayout } from '@/components/MainLayout';
import { SEO } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useCreateCustomReport, useUpdateCustomReport, useCustomReport } from '@/hooks/useCustomReports';
import { useTemplateConfig } from '@/hooks/useReportTemplates';
import { Step1DataSources } from './components/ReportStep1DataSources';
import { Step2Grouping } from './components/ReportStep2Grouping';
import { Step3Visualization } from './components/ReportStep3Visualization';
import { ReportLivePreview } from './components/ReportLivePreview';
import type { BuilderStep, BuilderState, ReportConfig, CustomReportWithConfig } from '@/types/custom-reports';
import { ReportConfigSchema } from '@/types/custom-reports';

type WizardMode = 'create' | 'edit';

const DEFAULT_STATE: BuilderState = {
  currentStep: 1,
  config: {
    dataSources: ['transactions'],
    dateRange: { type: 'this_month' },
    filters: {
      transactionType: 'all',
      status: 'all',
    },
    groupBy: null,
    compareWith: { enabled: false },
    metrics: { amount: 'sum' },
    visualizationType: 'bar',
    chartConfig: {
      showLegend: true,
      showDataLabels: true,
    },
  },
  reportName: '',
  reportDescription: '',
  isFavorite: false,
  isPublic: false,
};

export function CustomReportBuilder({ mode = 'create' }: { mode?: WizardMode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const createReport = useCreateCustomReport();
  const updateReport = useUpdateCustomReport();

  // Get the report ID to customize from (for "Customize" button)
  const fromReportId = searchParams.get('from');

  // Fetch existing report when in edit mode or when customizing from existing
  const { data: existingReport } = useCustomReport(id || '', {
    enabled: (mode === 'edit' && !!id) || (!!fromReportId && mode === 'create'),
  });

  // Fetch the "from" report for customization
  const { data: fromReport } = useCustomReport(fromReportId || '', {
    enabled: !!fromReportId && mode === 'create',
  });

  const [state, setState] = useState<BuilderState>(() => {
    // Start with template if provided
    const templateId = searchParams.get('template');
    const templateConfig = templateId ? useTemplateConfig(templateId) : null;

    // Start with template or default
    return {
      ...DEFAULT_STATE,
      config: templateConfig || DEFAULT_STATE.config,
    };
  });

  // Live preview visibility state
  const [showPreview, setShowPreview] = useState(false);

  // Update state when data is loaded
  useEffect(() => {
    // Start with existing report if editing
    if (existingReport && mode === 'edit') {
      setState({
        currentStep: 1,
        config: existingReport.config,
        reportName: existingReport.name,
        reportDescription: existingReport.description || '',
        isFavorite: existingReport.is_favorite,
        isPublic: existingReport.is_public,
      });
    }

    // Start with "from" report config when customizing (create mode)
    if (fromReport && mode === 'create') {
      setState({
        ...DEFAULT_STATE,
        config: fromReport.config,
        reportName: '', // Don't copy the name, let user create new one
        reportDescription: '',
      });
    }
  }, [existingReport, fromReport, mode]);

  const [errors, setErrors] = useState<Record<number, string>>({});

  const updateState = (updates: Partial<BuilderState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  const updateConfig = (configUpdates: Partial<ReportConfig>) => {
    setState(prev => ({
      ...prev,
      config: { ...prev.config, ...configUpdates },
    }));
  };

  const goToStep = (step: BuilderStep) => {
    // Validate current step before proceeding
    if (step > state.currentStep) {
      const validationError = validateStep(state.currentStep);
      if (validationError) {
        setErrors(prev => ({ ...prev, [state.currentStep]: validationError }));
        return;
      }
    }

    setErrors({});
    updateState({ currentStep: step });
  };

  const validateStep = (step: BuilderStep): string | null => {
    switch (step) {
      case 1:
        if (!state.config.dataSources || state.config.dataSources.length === 0) {
          return 'Selecione pelo menos uma fonte de dados';
        }
        break;
      case 2:
        // Step 2 has no required fields
        break;
      case 3:
        if (!state.reportName.trim()) {
          return 'Nome do relatório é obrigatório';
        }
        try {
          ReportConfigSchema.partial().parse(state.config);
        } catch {
          return 'Configuração do relatório inválida';
        }
        break;
    }
    return null;
  };

  const handleSave = () => {
    const validationError = validateStep(3);
    if (validationError) {
      setErrors({ 3: validationError });
      return;
    }

    const reportData = {
      name: state.reportName,
      description: state.reportDescription,
      is_favorite: state.isFavorite,
      is_public: state.isPublic,
      config: state.config,
    };

    if (mode === 'edit' && existingReport) {
      updateReport.mutate(
        { id: existingReport.id, data: reportData },
        {
          onSuccess: () => {
            navigate(`/reports/custom/${existingReport.id}`);
          },
        }
      );
    } else {
      createReport.mutate(reportData, {
        onSuccess: (data) => {
          navigate(`/reports/custom/${data.id}`);
        },
      });
    }
  };

  const handleSaveAndView = () => {
    handleSave();
  };

  const progress = (state.currentStep / 3) * 100;

  // Keyboard navigation handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        return;
      }

      // Escape: cancel and go back to reports list
      if (e.key === 'Escape') {
        e.preventDefault();
        navigate('/reports/custom');
        return;
      }

      // Enter: save on step 3 (if form is valid)
      if (e.key === 'Enter' && state.currentStep === 3 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const validationError = validateStep(3);
        if (!validationError) {
          handleSave();
        }
        return;
      }

      // Ctrl/Cmd + Enter: save and view on step 3
      if ((e.key === 'Enter' && e.ctrlKey) || (e.key === 'Enter' && e.metaKey)) {
        if (state.currentStep === 3) {
          e.preventDefault();
          const validationError = validateStep(3);
          if (!validationError) {
            handleSaveAndView();
          }
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state, navigate]);

  return (
    <MainLayout>
      <SEO
        title={mode === 'edit' ? 'Editar Relatório' : 'Novo Relatório Personalizado'}
        description={mode === 'edit' ? 'Altere as configurações do relatório' : 'Configure seu relatório em 3 passos simples'}
      />
      <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/reports/custom')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">
            {mode === 'edit' ? 'Editar Relatório' : 'Novo Relatório Personalizado'}
          </h1>
          <p className="text-muted-foreground">
            {mode === 'edit' ? 'Altere as configurações do relatório' : 'Configure seu relatório em 3 passos simples'}
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Passo {state.currentStep} de 3</span>
          <span>{Math.round(progress)}% completo</span>
        </div>
        <Progress value={progress} />
      </div>

      {/* Step Content */}
      <Card>
        <CardHeader>
          <CardTitle>
            {state.currentStep === 1 && 'Fontes de Dados e Filtros'}
            {state.currentStep === 2 && 'Agrupamento e Comparação'}
            {state.currentStep === 3 && 'Visualização e Salvar'}
          </CardTitle>
          <CardDescription>
            {state.currentStep === 1 && 'Selecione os dados que deseja analisar e aplique filtros'}
            {state.currentStep === 2 && 'Defina como os dados devem ser agrupados e compare períodos'}
            {state.currentStep === 3 && 'Escolha a visualização, nomeie e salve seu relatório'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {errors[state.currentStep] && (
            <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{errors[state.currentStep]}</p>
            </div>
          )}

          {state.currentStep === 1 && (
            <Step1DataSources
              config={state.config}
              updateConfig={updateConfig}
            />
          )}

          {state.currentStep === 2 && (
            <Step2Grouping
              config={state.config}
              updateConfig={updateConfig}
            />
          )}

          {state.currentStep === 3 && (
            <Step3Visualization
              config={state.config}
              reportName={state.reportName}
              reportDescription={state.reportDescription}
              isFavorite={state.isFavorite}
              isPublic={state.isPublic}
              updateConfig={updateConfig}
              updateState={updateState}
            />
          )}
        </CardContent>
      </Card>

      {/* Live Preview - shown after step 2 when user has selected meaningful options */}
      {state.currentStep >= 2 && (
        <ReportLivePreview
          config={state.config}
          isVisible={showPreview}
          onToggle={() => setShowPreview(!showPreview)}
        />
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => goToStep(Math.max(1, state.currentStep - 1) as BuilderStep)}
          disabled={state.currentStep === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>

        {state.currentStep < 3 ? (
          <Button onClick={() => goToStep((state.currentStep + 1) as BuilderStep)}>
            Próximo
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleSave}>
              Salvar
            </Button>
            <Button onClick={handleSaveAndView}>
              Salvar e Ver
            </Button>
          </div>
        )}
      </div>
    </div>
    </MainLayout>
  );
}
